import { useEffect, useState } from "react";
import api, { euro, fmtDate, apiError } from "@/lib/api";
import { useFormErrors, V, errSummary } from "@/lib/formErrors";
import { SectionTitle } from "@/components/common";
import { Modal, Field, Select, Row } from "@/pages/Notizie";
import { toast } from "sonner";
import { LongTextarea, NoteView } from "@/components/LongText";
import { FileSignature, Plus, User, Building2, CalendarClock, Euro, Pencil, FileDown, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const GRUPPI = [
  ["preparazione", "⚙️", "In preparazione", "bg-sky-50 border-sky-200"],
  ["date", "📅", "Date da definire", "bg-amber-50 border-amber-200"],
  ["fissati", "✅", "Fissati", "bg-emerald-50 border-emerald-200"],
  ["subordinati", "🟠", "Subordinati", "bg-orange-50 border-orange-200"],
  ["chiusi", "🏁", "Rogitati / Chiusi", "bg-slate-50 border-slate-200"],
];
import { scaricaFile } from "@/components/PropostaAzioni";

const STATI = [
  ["da_definire", "Da definire"],
  ["in_corso", "In corso"],
  ["firmato", "Firmato"],
  ["rogitato", "Rogitato"],
  ["annullato", "Annullato"],
];
const STATO_CLS = {
  da_definire: "text-slate-500 bg-slate-100 border-slate-300",
  in_corso: "text-[#FC82ED] bg-[#FC82ED]/10 border-[#FC82ED]/30",
  firmato: "text-blue-600 bg-blue-400/10 border-blue-400/30",
  rogitato: "text-emerald-600 bg-emerald-400/10 border-emerald-400/30",
  annullato: "text-slate-400 bg-slate-100 border-slate-300 line-through",
};
const STATO_LABEL = Object.fromEntries(STATI);
const empty = { venditore: "", acquirente: "", immobile_titolo: "", importo: "", caparra: "", data_preliminare: "", data_rogito: "", stato: "da_definire", note: "" };

export default function Preliminari() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {form, id}
  const [saving, setSaving] = useState(false);
  const [fStato, setFStato] = useState("preparazione");
  const nav = useNavigate();
  const { canFinance } = useAuth();

  const load = () => api.get("/preliminari").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => setModal({ form: { ...empty }, id: null });
  const openEdit = (p) => setModal({
    id: p.id,
    form: {
      venditore: p.venditore || "", acquirente: p.acquirente || "", immobile_titolo: p.immobile_titolo || p.immobile || "",
      importo: p.importo ?? "", caparra: p.caparra ?? "", data_preliminare: p.data_preliminare || "",
      data_rogito: p.data_rogito || "", stato: p.stato || "da_definire", note: p.note || "",
      notaio: p.notaio || "", notaio_comune: p.notaio_comune || "", notaio_via: p.notaio_via || "", notaio_civico: p.notaio_civico || "",
      annullato_motivo: p.annullato_motivo || "", data_incasso_provvigione: p.data_incasso_provvigione || "",
    },
  });

  const { errors, clear, validate, applyServer, summary } = useFormErrors();
  const setF = (k, v) => { setModal((m) => ({ ...m, form: { ...m.form, [k]: v } })); clear(k); };
  const shown = fStato ? items.filter((p) => p.gruppo === fStato) : [];
  const fase = async (p, f) => {
    try { await api.put(`/preliminari/${p.id}`, { fase: f, ...(f === "date" && p.stato === "da_definire" ? { stato: "in_corso" } : {}) }); toast.success(f === "fissato" ? "Preliminare fissato" : "Spostato in Date da definire"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    const f = modal.form;
    const errs = validate({ venditore: [V.required], acquirente: [V.required], immobile_titolo: [V.required], importo: [V.nonNegative], caparra: [V.nonNegative] }, f);
    if (Object.keys(errs).length) return toast.error(errSummary(Object.keys(errs).length));
    setSaving(true);
    const payload = {
      ...f,
      importo: f.importo === "" ? null : Number(f.importo),
      caparra: f.caparra === "" ? null : Number(f.caparra),
    };
    try {
      if (modal.id) await api.put(`/preliminari/${modal.id}`, payload);
      else await api.post(`/preliminari`, payload);
      toast.success(modal.id ? "Preliminare aggiornato" : "Preliminare creato");
      if (payload.stato === "firmato" && payload.data_rogito) toast.success("Rogito programmato automaticamente", { description: "Lo trovi nella sezione Rogiti." });
      setModal(null);
      load();
    } catch (e) { if (applyServer(e)) { setSaving(false); return; } toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <div className="fade-up" data-testid="preliminari-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <SectionTitle sub="Contratti preliminari (compromessi): parti, importo, caparra, scadenze e data del rogito.">Preliminari</SectionTitle>
        <button data-testid="preliminare-new-btn" onClick={openNew}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm glow-pink shrink-0">
          <Plus size={16} /> Nuovo preliminare
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-5 gap-3 mt-4" data-testid="preliminari-riquadri">
        {GRUPPI.map(([k, ico, label, cls]) => {
          const its = items.filter((p) => p.gruppo === k);
          return (
            <button key={k} data-testid={`prel-box-${k}`} aria-expanded={fStato === k} onClick={() => setFStato(fStato === k ? null : k)}
              className={`rounded-2xl border-2 p-3 sm:p-4 text-left transition-transform hover:-translate-y-0.5 ${cls} ${fStato === k ? "ring-2 ring-[#FC82ED]" : ""}`}>
              <div className="flex items-start gap-1 text-xs 2xl:text-sm font-bold"><span className="text-sm">{ico}</span><span className="leading-tight [word-break:keep-all]">{label}</span><ChevronDown size={14} className={`ml-auto shrink-0 ${fStato === k ? "rotate-180" : ""}`} /></div>
              <div className="mono text-3xl font-extrabold mt-2 text-[#111111]" data-testid={`prel-count-${k}`}>{its.length}</div>
              <div className="text-[11px] text-slate-600"><span className="mono font-semibold">{euro(its.reduce((a, p) => a + (p.provv_totale_calc || 0), 0))}</span> provvigioni</div>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="space-y-2 mt-4">{[...Array(3)].map((_, i) => <div key={i} className="skeleton h-24 rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <div className="card-immo p-12 text-center mt-4">
          <div className="w-14 h-14 rounded-2xl bg-[#FC82ED]/12 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED] mx-auto mb-4"><FileSignature size={26} /></div>
          <p className="text-[var(--muted)] text-sm">Nessun preliminare. Vengono creati automaticamente all'accettazione di una proposta, oppure aggiungine uno manualmente.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          {shown.map((p) => (
            <div key={p.id} data-testid={`preliminare-${p.id}`} className="card-immo p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold text-sm flex items-center gap-2 min-w-0">
                  <Building2 size={15} className="text-[#FC82ED] shrink-0" /> <span className="truncate">{p.immobile_titolo || p.immobile || "Immobile"}</span>
                </div>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase border shrink-0 ${STATO_CLS[p.stato] || STATO_CLS.da_definire}`}>{STATO_LABEL[p.stato] || p.stato}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-600">
                {p.venditore && <span className="inline-flex items-center gap-1"><User size={12} /> Vend.: <b className="text-[#111111]">{p.venditore}</b></span>}
                {p.acquirente && <span className="inline-flex items-center gap-1"><User size={12} /> Acq.: <b className="text-[#111111]">{p.acquirente}</b></span>}
                {p.agente && <span className="inline-flex items-center gap-1"><User size={12} /> Agente: <b className="text-[#111111]">{p.agente}</b></span>}
                {p.mancanti > 0 && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold border text-amber-600 bg-amber-100 border-amber-300">{p.mancanti} da completare</span>}
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs">
                {p.importo != null && <span className="inline-flex items-center gap-1 text-slate-600"><Euro size={12} /> Prezzo: <b className="text-[#111111]">{euro(p.importo)}</b></span>}
                {p.caparra != null && <span className="inline-flex items-center gap-1 text-slate-600">Caparra: <b className="text-[#111111]">{euro(p.caparra)}</b></span>}
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-[var(--muted)]">
                {p.data_preliminare && <span className="inline-flex items-center gap-1"><CalendarClock size={12} /> Prelim.: {fmtDate(p.data_preliminare)}</span>}
                {p.data_rogito && <span className="inline-flex items-center gap-1"><CalendarClock size={12} /> Rogito: {fmtDate(p.data_rogito)}</span>}
                {p.notaio && <span data-testid={`preliminare-notaio-${p.id}`}>Notaio: {[p.notaio, p.notaio_via ? [p.notaio_via, p.notaio_civico].filter(Boolean).join(" ") : "", p.notaio_comune].filter(Boolean).join(", ")}</span>}
                {p.annullato_motivo && <span className="text-red-500">Annullato: {p.annullato_motivo}</span>}
              </div>
              {p.proposta_firmata_file?.id && (
                <button data-testid={`preliminare-firmata-${p.id}`} onClick={() => scaricaFile(p.proposta_firmata_file)}
                  className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[var(--border)] bg-white text-xs font-semibold"><FileDown size={13} /> Proposta firmata</button>
              )}
              {p.note && <NoteView text={p.note} lines={2} className="text-xs text-[var(--muted)] mt-2" />}
              <div className="mt-2 text-xs flex flex-wrap gap-x-4 gap-y-1" data-testid={`prel-provv-${p.id}`}>
                <span>Provv. venditore <b className="mono">{euro(p.provv_venditore_calc)}</b> {p.incasso_venditore ? <span className="text-emerald-600 font-semibold">incassata</span> : <span className="text-[var(--muted)]">da incassare</span>}</span>
                <span>Provv. acquirente <b className="mono">{euro(p.provv_acquirente_calc)}</b> {p.incasso_acquirente ? <span className="text-emerald-600 font-semibold">incassata</span> : <span className="text-[var(--muted)]">da incassare</span>}</span>
                {p.mutuo_fino_al && <span className="text-orange-700 font-semibold">Mutuo fino al {fmtDate(p.mutuo_fino_al)}</span>}
              </div>
              <div className="mt-3 pt-3 border-t border-[var(--border)] flex justify-end gap-3 flex-wrap">
                {p.gruppo === "preparazione" && <button data-testid={`prel-avanti-${p.id}`} onClick={() => fase(p, "date")} className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-[#FC82ED] text-black">→ Date da definire</button>}
                {p.gruppo === "date" && <button data-testid={`prel-fissa-${p.id}`} onClick={() => (p.data_preliminare && p.data_rogito ? fase(p, "fissato") : (toast.info("Inserisci la data del preliminare e del rogito"), openEdit(p)))} className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-[#FC82ED] text-black">→ Fissato</button>}
                {p.gruppo === "fissati" && <button data-testid={`prel-registra-${p.id}`} onClick={() => nav(`/rogiti?da_preliminare=${p.id}`)} className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-emerald-600 text-white">Registra rogito/vendita</button>}
                <button data-testid={`preliminare-edit-${p.id}`} onClick={() => openEdit(p)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#FC82ED] hover:underline"><Pencil size={13} /> Modifica</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <Modal title={modal.id ? "Modifica preliminare" : "Nuovo preliminare"} testid="preliminare-modal" ultimoId={modal.id} summary={summary}
          onClose={() => setModal(null)} onSave={save} saving={saving} scheda value={modal.form} onRestore={(v) => setModal((m) => ({ ...m, form: v }))}>
          <Row>
            <Field label="Venditore" testid="prelim-venditore" required error={errors.venditore} value={modal.form.venditore} onChange={(e) => setF("venditore", e.target.value)} />
            <Field label="Acquirente" testid="prelim-acquirente" required error={errors.acquirente} value={modal.form.acquirente} onChange={(e) => setF("acquirente", e.target.value)} />
          </Row>
          <Field label="Immobile" testid="prelim-immobile" required error={errors.immobile_titolo} value={modal.form.immobile_titolo} onChange={(e) => setF("immobile_titolo", e.target.value)} />
          <Row>
            <Field label="Prezzo (€)" testid="prelim-importo" type="euro" error={errors.importo} value={modal.form.importo} onChange={(e) => setF("importo", e.target.value)} />
            <Field label="Caparra (€)" testid="prelim-caparra" type="euro" error={errors.caparra} value={modal.form.caparra} onChange={(e) => setF("caparra", e.target.value)} />
          </Row>
          <Row>
            <Field label="Data preliminare" testid="prelim-data-preliminare" type="date" value={modal.form.data_preliminare} onChange={(e) => setF("data_preliminare", e.target.value)} />
            <Field label="Data rogito prevista" testid="prelim-data-rogito" type="date" value={modal.form.data_rogito} onChange={(e) => setF("data_rogito", e.target.value)} />
          </Row>
          <div>
            <Field label="Data incasso provvigione" testid="prelim-data-incasso-provv" type="date" disabled={!canFinance}
              value={modal.form.data_incasso_provvigione || modal.form.data_preliminare || ""} onChange={(e) => setF("data_incasso_provvigione", e.target.value)} />
            <p className="text-[11px] text-[var(--muted)] mt-1" data-testid="prelim-data-incasso-hint">Le provvigioni si incassano al preliminare: se non la cambi vale la data del preliminare.{!canFinance && " Modificabile solo da Soci e Segreteria."} La data del rogito non sposta l'incasso.</p>
          </div>
          <Select label="Stato" testid="prelim-stato" value={modal.form.stato} onChange={(e) => setF("stato", e.target.value)} opts={STATI} />
          {modal.form.stato === "annullato" && (
            <Field label="Motivo annullamento" testid="prelim-annullato-motivo" value={modal.form.annullato_motivo || ""} onChange={(e) => setF("annullato_motivo", e.target.value)} />
          )}
          <Row>
            <Field label="Notaio (nome)" testid="prelim-notaio" value={modal.form.notaio || ""} onChange={(e) => setF("notaio", e.target.value)} />
            <Field label="Comune notaio" testid="prelim-notaio-comune" value={modal.form.notaio_comune || ""} onChange={(e) => setF("notaio_comune", e.target.value)} />
          </Row>
          <Row>
            <Field label="Via notaio" testid="prelim-notaio-via" value={modal.form.notaio_via || ""} onChange={(e) => setF("notaio_via", e.target.value)} />
            <Field label="Civico" testid="prelim-notaio-civico" value={modal.form.notaio_civico || ""} onChange={(e) => setF("notaio_civico", e.target.value)} />
          </Row>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Note</label>
            <LongTextarea data-testid="prelim-note" value={modal.form.note} onChange={(e) => setF("note", e.target.value)} rows={2}
              className="w-full px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm" />
          </div>
          {modal.form.stato === "firmato" && !modal.form.data_rogito && (
            <p className="text-[11px] text-amber-600">Imposta una data rogito per programmare automaticamente il rogito.</p>
          )}
        </Modal>
      )}
    </div>
  );
}
